import { useState } from "react";
import axios from "axios";
import config from "../../config";
import { fieldError, indianMobile10 } from "../../lib/validation";
import OtpActions from "./OtpActions";

function detailMessage(err) {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || x).join(", ");
  if (typeof d === "object" && d.message) return d.message;
  return "Something went wrong";
}

export default function ForgotPassword({ onClose }) {
  const [step, setStep] = useState("id"); // id | otp | done
  const [mode, setMode] = useState("email");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [hint, setHint] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    setError("");
    const err = mode === "email"
      ? fieldError({ email })
      : fieldError({ phone });
    if (err) { setError(err); return; }
    setLoading(true);
    try {
      const body = mode === "email" ? { email: email.trim() } : { phone: indianMobile10(phone) };
      const { data } = await axios.post(`${config.API}/auth/password/forgot`, body);
      setHint(data.message || "OTP sent");
      setStep("otp");
    } catch (err) {
      setError(detailMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const reset = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError("Passwords do not match."); return; }
    const err = fieldError({ password });
    if (err) { setError(err); return; }
    setLoading(true);
    try {
      const body = {
        code,
        new_password: password,
        ...(mode === "email" ? { email: email.trim() } : { phone: indianMobile10(phone) }),
      };
      await axios.post(`${config.API}/auth/password/reset`, body);
      setStep("done");
    } catch (err) {
      setError(detailMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-4 rounded-2xl border border-[#673c96]/15 p-4 bg-[#fdfcff]" data-testid="forgot-password">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-[#311b52]">Reset password</h3>
        {onClose && (
          <button type="button" className="text-xs text-[#673c96]" onClick={onClose}>Close</button>
        )}
      </div>

      {step === "id" && (
        <form onSubmit={send} className="space-y-3">
          <div className="flex gap-2">
            <button type="button" onClick={() => setMode("email")}
              className={`flex-1 rounded-full py-1.5 text-xs font-semibold ${mode === "email" ? "bg-[#311b52] text-white" : "border border-[#673c96]/20 text-[#673c96]"}`}>
              Email
            </button>
            <button type="button" onClick={() => setMode("phone")}
              className={`flex-1 rounded-full py-1.5 text-xs font-semibold ${mode === "phone" ? "bg-[#311b52] text-white" : "border border-[#673c96]/20 text-[#673c96]"}`}>
              Mobile
            </button>
          </div>
          {mode === "email" ? (
            <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="Registered email" className="w-full rounded-xl border border-[#673c96]/20 px-4 py-2.5 text-sm" />
          ) : (
            <input required value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
              maxLength={10} placeholder="10-digit mobile" className="w-full rounded-xl border border-[#673c96]/20 px-4 py-2.5 text-sm" />
          )}
          <button disabled={loading} className="w-full bg-[#673c96] text-white rounded-full py-2.5 text-sm font-semibold">
            {loading ? "Sending…" : "Send OTP"}
          </button>
        </form>
      )}

      {step === "otp" && (
        <form onSubmit={reset} className="space-y-3">
          <p className="text-xs text-[#673c96]">{hint}</p>
          <input required value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit OTP"
            className="w-full rounded-xl border border-[#673c96]/20 px-4 py-2.5 text-sm tracking-widest text-center" />
          <input required type="password" minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="New password (min 8)" className="w-full rounded-xl border border-[#673c96]/20 px-4 py-2.5 text-sm" />
          <input required type="password" minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)}
            placeholder="Confirm new password" className="w-full rounded-xl border border-[#673c96]/20 px-4 py-2.5 text-sm" />
          <button disabled={loading} className="w-full bg-[#f37721] text-white rounded-full py-2.5 text-sm font-semibold">
            {loading ? "Saving…" : "Set new password"}
          </button>
          <OtpActions
            loading={loading}
            changeLabel={mode === "phone" ? "Change number" : "Change email"}
            onChangeTarget={() => { setStep("id"); setCode(""); }}
            onResend={async () => {
              const body = mode === "email" ? { email: email.trim() } : { phone: indianMobile10(phone) };
              const { data } = await axios.post(`${config.API}/auth/password/forgot`, body);
              setHint(data.message || "OTP sent");
            }}
          />
        </form>
      )}

      {step === "done" && (
        <p className="text-sm text-emerald-700">Password updated. You can sign in now.</p>
      )}

      {error && <p className="mt-2 text-xs text-[#ee4036]">{error}</p>}
    </div>
  );
}
